import { TableProps, Table, TableColumnProps } from 'antd';
import { useTranslation } from 'translations/hooks';

import { GROUP_TYPE_TO_DATA_MAPPER } from 'business/shift-report/indicators/pages/indicator-analyze/components/display-container/format-data';
import { getIndicatorName } from 'business/shift-report/indicators/pages/indicator-analyze/get-indicator-name';
import { useIndicatorAnalyzeFilters } from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters';
import { useLegendFilters } from 'business/shift-report/indicators/pages/indicator-analyze/providers/legend-filters';
import { IndicatorMetadataDto, IndicatorValuesDto } from 'generated/apiSchemas';

type IndicatorAnalyzeTableProps = {
  indicatorAnalyzeData: IndicatorValuesDto['aggregated'] | undefined;
  indicatorSeries: IndicatorMetadataDto[] | undefined;
};

const COLUMN_WIDTH = 150;
const SCROLL_TRIGGER_VALUE = 500;

export const IndicatorAnalyzeTable = ({
  indicatorAnalyzeData,
  indicatorSeries,
}: IndicatorAnalyzeTableProps) => {
  const { t, language } = useTranslation();
  const filters = useIndicatorAnalyzeFilters();
  const { legendFilters } = useLegendFilters();

  const firstColumnByDate = {
    title: t('reportAnalyze.indicatorAnalyze.groupByOptions', {
      context: filters.groupByType,
    }),
    dataIndex: 'group',
    key: 'group',
    render: (value) => {
      return GROUP_TYPE_TO_DATA_MAPPER[filters.groupByType](value);
    },
    width: COLUMN_WIDTH,
  } satisfies TableColumnProps;

  const firstColumnByRing = {
    title: t('reportAnalyze.indicatorAnalyze.groupByOptions', {
      context: 'ring',
    }),
    dataIndex: 'group',
    key: 'group',
    width: COLUMN_WIDTH,
  } satisfies TableColumnProps;

  const columns = [
    filters.filterType === 'date' ? firstColumnByDate : firstColumnByRing,
    ...(indicatorSeries
      ? indicatorSeries
          .filter(
            (serie) =>
              legendFilters.allIndicators ||
              legendFilters.selectedIndicators.includes(serie.id),
          )
          .map((serie) => {
            return {
              title: `${getIndicatorName(indicatorSeries, serie.id, language)} (${serie.unit})`,
              dataIndex: serie.id,
              key: serie.id,
              width: COLUMN_WIDTH,
              render: (value) => Number(value).toLocaleString(language),
            } satisfies TableColumnProps;
          })
      : []),
  ] satisfies TableProps['columns'];

  return (
    <Table
      columns={columns}
      dataSource={indicatorAnalyzeData}
      pagination={false}
      scroll={{ y: SCROLL_TRIGGER_VALUE }}
      bordered
      size="small"
    />
  );
};
